import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { getrapportwell } from "../Redux/Actions/RapportActions";
import { getrapporttwin } from "../Redux/Actions/RapportTwinAction";
import { getrapportspintoss } from "../Redux/Actions/RapportSpintossAction";
import axios from 'axios';
import { Table, Container, Row, Col, Form, Button, Modal, Card, Spinner, Alert } from "react-bootstrap";
import { BarChart, PieChart, LineChart } from '@mui/x-charts';
import "./AdvancedAnalytics.css";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const emptyArray = [];
const emptyObject = {};

function AdvancedAnalytics() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = useSelector(state => state.AuthReducer.user);

  // States for filters
  const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedReportType, setSelectedReportType] = useState("All");

  // New states for Margin Calculation (COGS)
  const [billsValises, setBillsValises] = useState([]);
  const [billsPin, setBillsPin] = useState([]);
  const [productsValises, setProductsValises] = useState([]);
  const [productsPin, setProductsPin] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [technicalSheets, setTechnicalSheets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // State for Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReportDetail, setSelectedReportDetail] = useState(null);

  const extractLocId = (loc) => {
    if (!loc) return null;
    if (typeof loc === "string") return loc;
    if (loc.$oid) return loc.$oid;
    if (loc._id) return loc._id.toString();
    return null;
  };

  // Selectors - Stabilized with shallowEqual to prevent re-renders
  const rapportsWell = useSelector((state) => state.RapportReducer?.rapports || emptyArray, shallowEqual);
  const rapportsTwin = useSelector((state) => 
    state.RapportTwinReducer?.RapportsTwin || state.RapportTwinReducer?.rapportsTwin || emptyArray, 
    shallowEqual
  );
  const rapportsSpintoss = useSelector((state) => 
    state.RapportSpintossReducer?.RapportsSpintoss || state.RapportSpintossReducer?.rapportsSpintoss || emptyArray, 
    shallowEqual
  );


  const fetchAllData = useCallback(async (start, end) => {
    if (!start || !end || loading) return;
    setLoading(true);
    setError("");
    try {
      const headers = { Authorization: token ? `Bearer ${token}` : "" };
      const params = { startDate: start, endDate: end };

      // 1. Fetch persistent metadata only if not already loaded (Internal check to avoid re-creating this function)
      // We use a functional update or just check the state directly (state is available in closure but might be stale, 
      // however for metadata like locations it's usually fine or we can use a ref)
      
      const tasks = [
        axios.get("/api/BillsValises/getAllBillsV", { headers, params }).catch(() => ({ data: [] })),
        axios.get("/api/InvoicesV/getInvoices", { headers, params: { ...params, populate: "subProductV" } }).catch(() => ({ data: [] })),
        axios.get("/api/Bills/getAllBills", { headers, params }).catch(() => ({ data: [] })),
        dispatch(getrapportwell(params)),
        dispatch(getrapporttwin(params)),
        dispatch(getrapportspintoss(params))
      ];

      // Only add metadata tasks if they haven't been loaded yet
      let metaStartIndex = -1;
      if (locations.length === 0) {
        metaStartIndex = tasks.length;
        tasks.push(axios.get("/api/ProductV/getProductV", { headers }).catch(() => ({ data: [] })));
        tasks.push(axios.get("/api/Locations/getAllLocations", { headers }).catch(() => ({ data: [] })));
        tasks.push(axios.get("/api/Product/getProduct", { headers }).catch(() => ({ data: [] })));
        tasks.push(axios.get("/api/TechnicalSheet/all-sheets", { headers }).catch(() => ({ data: [] })));
      }

      const results = await Promise.all(tasks);

      // Map results back to states
      if (results[0].data) setBillsValises(results[0].data);
      if (results[1].data) setInvoices(results[1].data.invoices || results[1].data);
      if (results[2].data) setBillsPin(results[2].data);
      
      if (metaStartIndex !== -1) {
        if (results[metaStartIndex].data) setProductsValises(results[metaStartIndex].data);
        if (results[metaStartIndex+1].data) {
          const filteredLocs = (results[metaStartIndex+1].data || []).filter(loc => {
            const nameLower = (loc.name || "").toLowerCase();
            return !nameLower.includes("family");
          });
          setLocations(filteredLocs);
        }
        if (results[metaStartIndex+2].data) setProductsPin(results[metaStartIndex+2].data);
        if (results[metaStartIndex+3].data) setTechnicalSheets(results[metaStartIndex+3].data);
      }

    } catch (err) {
      console.error("Error fetching margin data:", err);
      setError("Erreur de chargement des données.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, dispatch]);

  const isDataReady = !loading && (billsValises.length > 0 || rapportsWell.length > 0 || rapportsTwin.length > 0 || rapportsSpintoss.length > 0);

  const isInitialMount = React.useRef(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/Accueil');
      return;
    }
    
    // Use a small timeout to avoid double-firing during React 18 strict mode
    // and to allow state to stabilize
    const timer = setTimeout(() => {
      fetchAllData(startDate, endDate);
    }, 100);

    return () => clearTimeout(timer);
  }, [user, navigate, fetchAllData, startDate, endDate]);

  // --- OPTIMIZED LOOKUP MAPS ---
  const invoiceLookup = useMemo(() => {
    const map = new Map();
    invoices.forEach(inv => {
      inv.items?.forEach(item => {
        const pId = extractLocId(item.productV || item.productId);
        const sId = extractLocId(item.subProductV || item.subProductId);
        const price = item.pricettc || (item.price * (1 + (item.tva || 0) / 100)) || 0;
        if (sId) map.set(sId, price);
        if (pId) map.set(pId, price);
      });
    });
    return map;
  }, [invoices]);

  const costLookup = useMemo(() => {
    const map = new Map();
    technicalSheets.forEach(s => {
      const pId = extractLocId(s.product);
      const sId = extractLocId(s.subProduct);
      if (sId) map.set(sId, s.totalCost || 0);
      if (pId) map.set(pId, s.totalCost || 0);
    });
    return map;
  }, [technicalSheets]);

  const findPurchasePrice = useCallback((productId, subProductId) => {
    const sId = extractLocId(subProductId);
    const pId = extractLocId(productId);
    return (sId && invoiceLookup.get(sId)) || (pId && invoiceLookup.get(pId)) || 0;
  }, [invoiceLookup]);

  const findProductionCost = useCallback((productId) => {
    const pId = extractLocId(productId);
    if (costLookup.has(pId)) return costLookup.get(pId);
    
    const prod = productsPin.find(p => extractLocId(p._id) === pId);
    if (prod) return (prod.price || 0) * 0.7;
    
    for (const p of productsPin) {
      const sp = p.subProducts?.find(s => extractLocId(s._id) === pId);
      if (sp) return (sp.price || 0) * 0.7;
    }
    return 0;
  }, [costLookup, productsPin]);

  const getProductDetailsValises = useCallback((item) => {
    const p = productsValises.find(x => x._id === item.id);
    if (p) {
      const cost = p.category === "SET" ? (p.subProductsV?.reduce((acc, sp) => acc + findPurchasePrice(p._id, sp._id), 0) || 0) : findPurchasePrice(p._id, null);
      return { cost, loc: extractLocId(p.localisation), name: p.name };
    }
    for (const parent of productsValises) {
      const sp = parent.subProductsV?.find(x => x._id === item.id);
      if (sp) return { cost: findPurchasePrice(parent._id, sp._id), loc: extractLocId(sp.localisation), name: sp.name };
    }
    return { cost: 0, loc: null, name: "Inconnu" };
  }, [productsValises, findPurchasePrice]);

  const getProductDetailsPin = useCallback((item) => {
    const p = productsPin.find(x => extractLocId(x._id) === extractLocId(item.id));
    if (p) return { cost: findProductionCost(p._id), loc: extractLocId(p.localisation), name: p.name };
    
    for (const parent of productsPin) {
      const sp = parent.subProducts?.find(x => extractLocId(x._id) === extractLocId(item.id));
      if (sp) return { cost: findProductionCost(sp._id), loc: extractLocId(sp.localisation), name: sp.name };
    }
    return { cost: 0, loc: null, name: "Inconnu" };
  }, [productsPin, findProductionCost]);



  // Pre-compute timestamps once for all filters (avoid creating moment objects per item)
  const rangeTs = useMemo(() => ({
    s: moment(startDate).startOf('day').valueOf(),
    e: moment(endDate).endOf('day').valueOf()
  }), [startDate, endDate]);

  const fastFilter = useCallback((data, dateField = "DateCollect") => {
    if (!Array.isArray(data) || data.length === 0) return [];
    const { s, e } = rangeTs;
    return data.filter(item => {
      if (item.status === "cancelled") return false;
      const raw = item[dateField] || item.date || item.DateCollect || item.createdAt;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      return t >= s && t <= e;
    });
  }, [rangeTs]);

  const filteredWell     = useMemo(() => fastFilter(rapportsWell),         [rapportsWell,     fastFilter]);
  const filteredTwin     = useMemo(() => {
    return fastFilter(rapportsTwin).filter(item => {
      const loc = (item.localisation || "").toLowerCase();
      return !loc.includes("family");
    });
  }, [rapportsTwin, fastFilter]);
  const filteredSpintoss = useMemo(() => fastFilter(rapportsSpintoss),      [rapportsSpintoss, fastFilter]);
  const filteredBills    = useMemo(() => fastFilter(billsValises, "date"),  [billsValises,     fastFilter]);
  const filteredPinBills = useMemo(() => fastFilter(billsPin, "date"),      [billsPin,         fastFilter]);

  // Grouping and Calculations
  const isBng = (b) => {
    const name = b.localisation?.name || b.localisation || "";
    const n = name.toString().toLowerCase();
    return n.includes('bagngo') || n.includes('bng');
  };
  const isEsp = (b) => {
    const name = b.localisation?.name || b.localisation || "";
    const n = name.toString().toLowerCase();
    if (n.includes('espace') || n.includes('e-space')) return true;
    const userName = (b.user?.name || b.userName || "").toString().toLowerCase();
    return userName.includes('espace') || userName.includes('e-space');
  };
  const ttLocs = [
    "tunisia mall", "manar city", "jardins d'el menzah", 
    "tp-tunisia mallmarge", "tp-manar citymarge", "tp-jardins d'el menzahmarge",
    "tp-tunisiamall", "tp-manarcity", "tp-lesjardinsdelmenzah"
  ];
  const isTwinToys = (b) => {
    const name = b.localisation?.name || b.localisation || "";
    const n = name.toString().toLowerCase().replace(/\s+/g, ''); // Remove spaces for comparison
    return ttLocs.some(loc => n.includes(loc.replace(/\s+/g, '')));
  };
  const isSpin = (b) => {
    const name = b.localisation?.name || b.localisation || "";
    const n = name.toString().toLowerCase();
    return n.includes('spintoss') || n.includes('spin');
  };

  // Single-pass split of filteredBills and filteredWell
  const { bagngoBills, espaceBills, bagngoReports, espaceReports, wellnessReports } = useMemo(() => {
    const bngB = [], espB = [], bngR = [], espR = [], wellR = [];
    filteredBills.forEach(b => { if (isBng(b)) bngB.push(b); else if (isEsp(b)) espB.push(b); });
    filteredWell.forEach(r => { if (isBng(r)) bngR.push(r); else if (isEsp(r)) espR.push(r); else wellR.push(r); });
    return { bagngoBills: bngB, espaceBills: espB, bagngoReports: bngR, espaceReports: espR, wellnessReports: wellR };
  }, [filteredBills, filteredWell]);

  const bagngoSales = useMemo(() => bagngoBills.reduce((a, b) => a + (b.total || 0), 0), [bagngoBills]);
  const espaceSales  = useMemo(() => espaceBills.reduce((a, b)  => a + (b.total || 0), 0), [espaceBills]);

  // Pre-build location name cache to avoid find() per item
  const locNameCache = useMemo(() => {
    const m = new Map();
    locations.forEach(l => m.set(extractLocId(l._id), l.name?.toLowerCase() || ""));
    return m;
  }, [locations]);

  const ttLocsNS = useMemo(() => ttLocs.map(l => l.replace(/\s+/g, '')), []);

  // --- SINGLE-PASS MARGIN STATS ---
  const marginStats = useMemo(() => {
    const stats = {
      BagnGo:   { sales: 0, cost: 0 },
      Espace:   { sales: 0, cost: 0 },
      Wellness: { sales: 0, cost: 0 },
      TwinPark: { sales: 0, cost: 0 },
      TwinToys: { sales: 0, cost: 0, locations: {} },
      Spintoss: { sales: 0, cost: 0 },
      Total:    { sales: 0, cost: 0 }
    };

    const resolveLoc = (raw) => {
      if (!raw) return "";
      let idStr = "";
      if (typeof raw === "string") {
        idStr = raw;
      } else if (raw.$oid) {
        idStr = raw.$oid;
      } else if (raw._id) {
        idStr = typeof raw._id === "string" ? raw._id : (raw._id.$oid || raw._id.toString());
      } else if (raw.name) {
        idStr = raw.name;
      } else {
        idStr = raw.toString();
      }
      let name = idStr.toLowerCase();
      if (/^[0-9a-fA-F]{24}$/.test(name)) name = locNameCache.get(name) || "";
      return name;
    };

    const classify = (name, b) => {
      const n = name.replace(/\s+/g, '');
      if (n.includes('bagngo') || n.includes('bng')) return 'BagnGo';
      if (n.includes('espace') || n.includes('e-space')) return 'Espace';
      if (b) {
        const userName = (b.user?.name || b.userName || "").toString().toLowerCase();
        if (userName.includes('espace') || userName.includes('e-space')) return 'Espace';
      }
      if (n.includes('spintoss') || n.includes('spin')) return 'Spintoss';
      if (ttLocsNS.some(l => n.includes(l))) return 'TwinToys';
      if (n.includes('twin')) return 'TwinPark';
      return 'Wellness';
    };

    // Valises bills
    filteredBills.forEach(bill => {
      let loc = resolveLoc(bill.localisation);
      bill.items?.forEach(item => {
        const details = getProductDetailsValises(item);
        const qty  = item.quantity || 1;
        const sale = (item.price || 0) * qty;
        const cost = details.cost * qty;
        let name = loc;
        if (!name || /^[0-9a-fA-F]{24}$/.test(name)) name = locNameCache.get(details.loc) || "";
        const cat = classify(name, bill);
        stats[cat].sales += sale;
        stats[cat].cost  += cost;
        if (cat === 'TwinToys') {
          let lKey = name.replace('tp-','').replace('marge','').trim().replace(/\b\w/g, c => c.toUpperCase());
          if (!stats.TwinToys.locations[lKey]) stats.TwinToys.locations[lKey] = { sales: 0, cost: 0 };
          stats.TwinToys.locations[lKey].sales += sale;
          stats.TwinToys.locations[lKey].cost  += cost;
        }
        stats.Total.sales += sale;
        stats.Total.cost  += cost;
      });
    });

    // Pin bills
    filteredPinBills.forEach(bill => {
      let loc = resolveLoc(bill.localisation);
      bill.items?.forEach(item => {
        const details = getProductDetailsPin(item);
        const qty  = item.quantity || 1;
        const sale = (item.price || 0) * qty;
        const cost = details.cost * qty;
        let name = loc;
        if (!name || /^[0-9a-fA-F]{24}$/.test(name)) name = locNameCache.get(details.loc) || "";
        const cat = classify(name, bill);
        if (cat === 'Wellness') { stats.Total.sales += sale; stats.Total.cost += cost; return; }
        stats[cat].sales += sale;
        stats[cat].cost  += cost;
        if (cat === 'TwinToys') {
          let lKey = name.replace('tp-','').replace('marge','').trim().replace(/\b\w/g, c => c.toUpperCase());
          if (!stats.TwinToys.locations[lKey]) stats.TwinToys.locations[lKey] = { sales: 0, cost: 0 };
          stats.TwinToys.locations[lKey].sales += sale;
          stats.TwinToys.locations[lKey].cost  += cost;
        }
        stats.Total.sales += sale;
        stats.Total.cost  += cost;
      });
    });

    return stats;
  }, [filteredBills, filteredPinBills, getProductDetailsValises, getProductDetailsPin, locNameCache, ttLocsNS]);

  const sumValues = (data, valField = "Amount", expField = "DailyExpenses") => {
    const totalV = data.reduce((acc, cur) => acc + (cur[valField] || 0), 0);
    const totalE = data.reduce((acc, cur) => acc + (cur[expField] || 0), 0);
    return { totalV, totalE };
  };

  const { wellTotals, bngTotals, espTotals, twinTotals, spinTotals } = useMemo(() => {
    return {
      wellTotals: sumValues(wellnessReports, "Amount", "MonthExpenses"),
      bngTotals: sumValues(bagngoReports, "Amount", "MonthExpenses"),
      espTotals: sumValues(espaceReports, "Amount", "MonthExpenses"),
      twinTotals: sumValues(filteredTwin, "TotalAmount", "DailyExpenses"),
      spinTotals: sumValues(filteredSpintoss, "TotalAmount", "DailyExpenses")
    };
  }, [wellnessReports, bagngoReports, espaceReports, filteredTwin, filteredSpintoss]);

  // Debug logs removed from hot path for performance

  // --- NEW ADVANCED ANALYTICS LOGIC ---

  // 1. Twin Park Categories Sum (MUST BE BEFORE Combined Incomes)
  const twinDetails = useMemo(() => {
    return filteredTwin.reduce((acc, curr) => {
      acc.PlayZone += (curr.PlayZoneStandard || 0) + (curr.PlayZoneSurveillé || 0);
      acc.Parents += (curr.AccesParent || 0);
      acc.PacksQR += (curr.PackQR || 0) + (curr.RechargeQR || 0);
      acc.PacksCard += (curr.PackCard || 0) + (curr.RechargeCard || 0);
      acc.TwinToys += (curr.TwinToys || 0);
      acc.Birthday += (curr.PackAnnif || 0);
      return acc;
    }, { PlayZone: 0, Parents: 0, PacksQR: 0, PacksCard: 0, TwinToys: 0, Birthday: 0 });
  }, [filteredTwin]);

  // CA Global = max(CA POS, CA Rapports) — on prend le plus élevé des deux sources
  const finalBagngoIncome   = Math.max(marginStats.BagnGo.sales,   bngTotals.totalV);
  const finalEspaceIncome   = Math.max(marginStats.Espace.sales,    espTotals.totalV);
  const finalWellnessIncome = Math.max(marginStats.Wellness.sales,  wellTotals.totalV);
  const finalSpintossIncome = Math.max(marginStats.Spintoss.sales,  spinTotals.totalV);
  // TwinToys : max entre ventes POS et part rapports TwinPark
  const finalTwinToysIncome = Math.max(marginStats.TwinToys.sales,  twinDetails.TwinToys);
  const finalTwinIncome     = Math.max(marginStats.TwinPark.sales,  twinTotals.totalV - twinDetails.TwinToys);

  // Global totals
  const globalIncome   = finalWellnessIncome + finalBagngoIncome + finalEspaceIncome + finalTwinIncome + finalTwinToysIncome + finalSpintossIncome;
  const globalExpenses = wellTotals.totalE + bngTotals.totalE + espTotals.totalE + twinTotals.totalE + spinTotals.totalE + marginStats.Total.cost;
  const globalNet      = globalIncome - globalExpenses;

  // 2. Wellness Ranking (Revenue per Chair)
  const wellnessRanking = useMemo(() => {
    const centers = wellnessReports.reduce((acc, curr) => {
      if (!acc[curr.localisation]) {
        acc[curr.localisation] = { name: curr.localisation, totalRev: 0, totalChairs: 0, count: 0 };
      }
      acc[curr.localisation].totalRev += (curr.Amount || 0);
      acc[curr.localisation].totalChairs += (curr.ChairNumber || 0);
      acc[curr.localisation].count += 1;
      return acc;
    }, {});

    return Object.values(centers).map(c => ({
      ...c,
      avgRev: c.totalRev / c.count,
      efficiency: c.totalChairs > 0 ? (c.totalRev / c.totalChairs) : 0
    })).sort((a, b) => b.efficiency - a.efficiency);
  }, [wellnessReports]);

  // 3. Day of Week Analysis (BagnGo, E-space, TwinToys)
  const dayOfWeekData = useMemo(() => {
    const days = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
    const stats = days.map(d => ({ 
      day: d, 
      BagnGo: 0, 
      Espace: 0, 
      TwinToys: 0,
      Wellness: 0,
      TwinPark: 0,
      Spintoss: 0
    }));

    // Helper to get day index (0 is Monday)
    const getDayIndex = (date) => {
      let d = moment(date).day(); // 0 is Sunday, 1 is Monday
      return d === 0 ? 6 : d - 1;
    };

    const spinBills = filteredPinBills.filter(isSpin);
    const ttBills   = filteredPinBills.filter(isTwinToys);
    const tpBills   = filteredPinBills.filter(b => !isSpin(b) && !isTwinToys(b) && b.localisation?.toString().toLowerCase().includes('twin'));

    // Batch process all data for day of week
    bagngoBills.forEach(b => { stats[getDayIndex(b.date || b.createdAt)].BagnGo += (b.total || 0); });
    bagngoReports.forEach(r => { stats[getDayIndex(r.DateCollect)].BagnGo += (r.Amount || 0); });
    espaceBills.forEach(b => { stats[getDayIndex(b.date || b.createdAt)].Espace += (b.total || 0); });
    espaceReports.forEach(r => { stats[getDayIndex(r.DateCollect)].Espace += (r.Amount || 0); });
    filteredTwin.forEach(r => { stats[getDayIndex(r.DateCollect)].TwinToys += (r.TwinToys || 0); });
    ttBills.forEach(b => { stats[getDayIndex(b.date || b.createdAt)].TwinToys += (b.total || 0); });
    wellnessReports.forEach(r => { stats[getDayIndex(r.DateCollect)].Wellness += (r.Amount || 0); });
    filteredSpintoss.forEach(r => { stats[getDayIndex(r.DateCollect)].Spintoss += (r.TotalAmount || 0); });
    spinBills.forEach(b => { stats[getDayIndex(b.date || b.createdAt)].Spintoss += (b.total || 0); });
    filteredTwin.forEach(r => { stats[getDayIndex(r.DateCollect)].TwinPark += ((r.TotalAmount || 0) - (r.TwinToys || 0)); });
    tpBills.forEach(b => { stats[getDayIndex(b.date || b.createdAt)].TwinPark += (b.total || 0); });

    return stats;
  }, [bagngoBills, bagngoReports, espaceBills, espaceReports, filteredTwin, wellnessReports, filteredSpintoss, filteredPinBills]);

  // 4. Data for Main Charts
  const categoryData = [
    { cat: 'Wellness', rev: finalWellnessIncome },
    { cat: 'BagnGo', rev: finalBagngoIncome },
    { cat: 'E-space', rev: finalEspaceIncome },
    { cat: 'Twin Park', rev: finalTwinIncome },
    { cat: 'Twin Toys', rev: finalTwinToysIncome },
    { cat: 'Spintoss', rev: finalSpintossIncome },
  ];

  const twinToysChartData = Object.keys(marginStats.TwinToys?.locations || {}).map(loc => ({
    label: loc,
    value: marginStats.TwinToys.locations[loc].sales
  }));

  // --- COMPARISON LOGIC ---
  const getPrevPeriodStats = useMemo(() => {
    const duration = moment(endDate).diff(moment(startDate), 'days') + 1;
    const prevStart = moment(startDate).subtract(duration, 'days').format('YYYY-MM-DD');
    const prevEnd = moment(startDate).subtract(1, 'days').format('YYYY-MM-DD');

    // Simple filter for prev period (we don't re-run the whole margin engine for speed, just reports)
    const filterPrev = (data, dateField = "DateCollect") => {
      if (!Array.isArray(data)) return [];
      const s = moment(prevStart).startOf('day');
      const e = moment(prevEnd).endOf('day');
      return data.filter(item => moment(item[dateField] || item.date || item.DateCollect).isBetween(s, e, null, '[]'));
    };

    const pWell = filterPrev(rapportsWell);
    const pTwin = filterPrev(rapportsTwin);
    const pSpin = filterPrev(rapportsSpintoss);
    
    return {
      wellness: pWell.reduce((acc, cur) => acc + (cur.Amount || 0), 0),
      twin: pTwin.reduce((acc, cur) => acc + (cur.TotalAmount || 0), 0),
      spintoss: pSpin.reduce((acc, cur) => acc + (cur.TotalAmount || 0), 0),
    };
  }, [startDate, endDate, rapportsWell, rapportsTwin, rapportsSpintoss]);

  const getEvolution = (current, previous) => {
    if (!previous || previous === 0) return { val: 0, up: true };
    const diff = ((current - previous) / previous) * 100;
    return { val: Math.abs(diff).toFixed(1), up: diff >= 0 };
  };

  // --- ENTITY SPECIFIC DAILY TRENDS ---
  const getDailyData = (reports, bills, dateField = "DateCollect") => {
    const dataMap = {};
    reports.forEach(r => {
      const d = moment(r[dateField] || r.DateCollect).format('DD/MM');
      dataMap[d] = (dataMap[d] || 0) + (r.Amount || r.TotalAmount || 0);
    });
    bills.forEach(b => {
      const d = moment(b.date || b.createdAt).format('DD/MM');
      dataMap[d] = (dataMap[d] || 0) + (b.total || 0);
    });
    return Object.keys(dataMap).sort().map(k => ({ date: k, value: dataMap[k] }));
  };

  const trendWellness = getDailyData(filteredWell, []);
  const trendBagnGo = getDailyData(bagngoReports, bagngoBills);
  const trendEspace = getDailyData(espaceReports, espaceBills);
  const trendTwin = getDailyData(filteredTwin, []);
  const trendSpintoss = getDailyData(filteredSpintoss, marginStats.Spintoss.sales > 0 ? [{date: moment().format(), total: marginStats.Spintoss.sales}] : []);

  // --- DAY OF WEEK DISTRIBUTION PER ENTITY ---
  const getDayOfWeekDist = (reports, bills, dateField = "DateCollect") => {
    const days = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
    const stats = [0, 0, 0, 0, 0, 0, 0];
    
    reports.forEach(r => {
      const d = moment(r[dateField] || r.DateCollect).day();
      const idx = d === 0 ? 6 : d - 1;
      stats[idx] += (r.Amount || r.TotalAmount || 0);
    });
    bills.forEach(b => {
      const d = moment(b.date || b.createdAt).day();
      const idx = d === 0 ? 6 : d - 1;
      stats[idx] += (b.total || 0);
    });
    
    return days.map((d, i) => ({ day: d, value: stats[i] }));
  };

  const distWell = getDayOfWeekDist(filteredWell, []);
  const distBng = getDayOfWeekDist(bagngoReports, bagngoBills);
  const distEsp = getDayOfWeekDist(espaceReports, espaceBills);
  const distSpin = getDayOfWeekDist(filteredSpintoss, filteredPinBills.filter(isSpin));


  const openDetail = (report, type) => {
    setSelectedReportDetail({ ...report, type });
    setShowDetailModal(true);
  };

  const renderDetailFields = () => {
    if (!selectedReportDetail) return null;
    const type = selectedReportDetail.type;
    
    // List of keys to exclude
    const exclude = ["_id", "owner", "createdAt", "updatedAt", "__v", "type", "image", "file"];
    
    return Object.keys(selectedReportDetail)
      .filter(key => !exclude.includes(key))
      .map(key => (
        <div key={key} className="detail-item">
          <span className="detail-label">{key}:</span>
          <span className="detail-value">{String(selectedReportDetail[key])}</span>
        </div>
      ));
  };

  if (!isDataReady) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', background: '#f4f7fe' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted fw-bold">Analyse des données en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="advanced-root">
      <Container fluid className="advanced-container">
        <div className="advanced-header">
          <h2>Analyse Avancée & Historique</h2>
          <p>Filtrez vos données par période et accédez aux détails complets des rapports.</p>
        </div>

        {/* Filters Card */}
        <Card className="filter-card">
          <Card.Body>
            <Row className="align-items-end">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date Début</Form.Label>
                  <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Date Fin</Form.Label>
                  <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Type de Rapport</Form.Label>
                  <Form.Select value={selectedReportType} onChange={(e) => setSelectedReportType(e.target.value)}>
                    <option value="All">Tous les rapports</option>
                    <option value="Wellness">Wellness</option>
                    <option value="TwinPark">Twin Park</option>
                    <option value="Spintoss">Spintoss</option>
                    <option value="BagnGo">BagnGo</option>
                    <option value="Espace">E-space</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Button variant="primary" className="w-100 refresh-btn" onClick={() => window.location.reload()}>
                  🔄 Actualiser
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Summary Row */}
        <Row className="summary-row g-4 mb-4">
          <Col lg={3}>
            <Card className="summary-card total-income">
              <div className="card-icon">💰</div>
              <div className="card-info">
                <h4>Total Recettes</h4>
                <p>{globalIncome.toFixed(3)} DT</p>
              </div>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="summary-card total-expenses">
              <div className="card-icon">📉</div>
              <div className="card-info">
                <h4>Total Dépenses</h4>
                <p>{globalExpenses.toFixed(3)} DT</p>
              </div>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="summary-card net-profit">
              <div className="card-icon">🏆</div>
              <div className="card-info">
                <h4>Bénéfice Net</h4>
                <p>{globalNet.toFixed(3)} DT</p>
              </div>
            </Card>
          </Col>
          <Col lg={3}>
            <Card className="summary-card avg-card">
              <div className="card-icon">📊</div>
              <div className="card-info">
                <h4>Moyenne/Jour</h4>
                <p>{(globalIncome / (moment(endDate).diff(moment(startDate), 'days') + 1)).toFixed(3)} DT</p>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Tableau Récapitulatif Global */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h5 className="mb-4">Récapitulatif de Performance par Entité</h5>
                <Table responsive hover className="align-middle text-center">
                  <thead className="bg-light">
                    <tr>
                      <th className="text-start">Entité</th>
                      <th>CA POS</th>
                      <th>CA Rapports</th>
                      <th>CA Global</th>
                      <th>Dépenses Op.</th>
                      <th>Coût Articles</th>
                      <th>Bénéfice Net</th>
                      <th>Marge (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'Wellness', pos: marginStats.Wellness.sales, rap: wellTotals.totalV, exp: wellTotals.totalE, cost: marginStats.Wellness.cost },
                      { name: 'BagnGo', pos: marginStats.BagnGo.sales, rap: bngTotals.totalV, exp: bngTotals.totalE, cost: marginStats.BagnGo.cost },
                      { name: 'E-space', pos: marginStats.Espace.sales, rap: espTotals.totalV, exp: espTotals.totalE, cost: marginStats.Espace.cost },
                      { name: 'Twin Park', pos: marginStats.TwinPark.sales, rap: twinTotals.totalV - twinDetails.TwinToys, exp: twinTotals.totalE, cost: marginStats.TwinPark.cost },
                      { name: 'Twin Toys', pos: marginStats.TwinToys.sales, rap: twinDetails.TwinToys, exp: 0, cost: marginStats.TwinToys.cost },
                      { name: 'Spintoss', pos: marginStats.Spintoss.sales, rap: spinTotals.totalV, exp: spinTotals.totalE, cost: marginStats.Spintoss.cost },
                    ].map(row => {
                      // CA Global = le plus élevé entre POS et Rapports
                      const totalCA = Math.max(row.pos, row.rap);
                      const totalExp = row.exp + row.cost;
                      const net = totalCA - totalExp;
                      const margin = totalCA > 0 ? (net / totalCA) * 100 : 0;
                      return (
                        <tr key={row.name}>
                          <td className="text-start fw-bold">{row.name}</td>
                          <td>{row.pos.toFixed(3)}</td>
                          <td>{row.rap.toFixed(3)}</td>
                          <td className="fw-bold text-primary">{totalCA.toFixed(3)}</td>
                          <td className="text-danger">{row.exp.toFixed(3)}</td>
                          <td className="text-warning">{row.cost.toFixed(3)}</td>
                          <td className={`fw-bold ${net >= 0 ? 'text-success' : 'text-danger'}`}>{net.toFixed(3)}</td>
                          <td>{margin.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                    <tr className="table-dark fw-bold">
                      <td className="text-start">TOTAL GLOBAL</td>
                      <td>{marginStats.Total.sales.toFixed(3)}</td>
                      <td>{(wellTotals.totalV + bngTotals.totalV + espTotals.totalV + twinTotals.totalV + spinTotals.totalV).toFixed(3)}</td>
                      <td>{globalIncome.toFixed(3)}</td>
                      <td>{(wellTotals.totalE + bngTotals.totalE + espTotals.totalE + twinTotals.totalE + spinTotals.totalE).toFixed(3)}</td>
                      <td>{marginStats.Total.cost.toFixed(3)}</td>
                      <td>{globalNet.toFixed(3)}</td>
                      <td>{(globalIncome > 0 ? (globalNet / globalIncome) * 100 : 0).toFixed(1)}%</td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Global Charts Row */}
        <Row className="mb-4 g-4">
          <Col lg={6}>
            <Card className="chart-card-advanced">
              <h3>Analyse des Ventes par Jour de la Semaine</h3>
              <p className="chart-subtitle">Cumul BagnGo, E-space et TwinToys</p>
              <div style={{ height: 350 }}>
                <BarChart
                  dataset={dayOfWeekData}
                  xAxis={[{ scaleType: 'band', dataKey: 'day' }]}
                  series={[
                    { dataKey: 'Wellness', label: 'Well', color: '#05cd99' },
                    { dataKey: 'BagnGo', label: 'Bng', color: '#f97316' },
                    { dataKey: 'Espace', label: 'Esp', color: '#0ea5e9' },
                    { dataKey: 'TwinPark', label: 'TP', color: '#4318FF' },
                    { dataKey: 'TwinToys', label: 'Toys', color: '#ec4899' },
                    { dataKey: 'Spintoss', label: 'Spin', color: '#8b5cf6' }
                  ]}
                  margin={{ top: 20, bottom: 30, left: 50, right: 10 }}
                />
              </div>
            </Card>
          </Col>
          <Col lg={6}>
            <Card className="chart-card-advanced">
              <h3>Répartition des Recettes par Catégorie</h3>
              <p className="chart-subtitle">Performance globale sur la période</p>
              <div style={{ height: 350 }}>
                <BarChart
                  dataset={categoryData}
                  xAxis={[{ scaleType: 'band', dataKey: 'cat' }]}
                  series={[
                    { dataKey: 'rev', label: 'Recettes', color: '#4318FF' }
                  ]}
                  layout="vertical"
                  margin={{ top: 20, bottom: 30, left: 60, right: 10 }}
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Advanced Insights Row */}
        <Row className="mb-4 g-4">
          {/* Twin Park Detailed Summary */}
          <Col lg={7}>
            <Card className="insight-card twinpark-insight">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Détails Revenus Twin Park</h3>
                <span className="badge bg-primary">Période sélectionnée</span>
              </div>
              <div className="insight-grid">
                <div className="insight-item">
                  <label>PlayZone (Std + Surv)</label>
                  <span className="text-primary">{twinDetails.PlayZone.toFixed(3)} DT</span>
                </div>
                <div className="insight-item">
                  <label>Accès Parents</label>
                  <span className="text-info">{twinDetails.Parents.toFixed(3)} DT</span>
                </div>
                <div className="insight-item">
                  <label>Packs & Recharges QR</label>
                  <span className="text-warning">{twinDetails.PacksQR.toFixed(3)} DT</span>
                </div>
                <div className="insight-item">
                  <label>Packs & Recharges Cartes</label>
                  <span className="text-secondary">{twinDetails.PacksCard.toFixed(3)} DT</span>
                </div>
                <div className="insight-item">
                  <label>TwinToys</label>
                  <span className="text-danger">{twinDetails.TwinToys.toFixed(3)} DT</span>
                </div>
                <div className="insight-item">
                  <label>Anniversaires</label>
                  <span className="text-success">{twinDetails.Birthday.toFixed(3)} DT</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                <span className="fw-bold text-muted">TOTAL TWIN PARK</span>
                <span className="fs-4 fw-bold text-primary">{twinTotals.totalV.toFixed(3)} DT</span>
              </div>
            </Card>
          </Col>

          {/* Wellness Ranking */}
          <Col lg={5}>
            <Card className="insight-card wellness-insight">
              <h3>Classement Efficacité Wellness</h3>
              <p className="chart-subtitle">Moyenne Recette par Chaise</p>
              <Table responsive className="ranking-table">
                <thead>
                  <tr>
                    <th>Centre</th>
                    <th>Chaises</th>
                    <th>Eff. (TND/Chaise)</th>
                  </tr>
                </thead>
                <tbody>
                  {wellnessRanking.map((c, i) => (
                    <tr key={i} className={i === 0 ? "top-rank" : ""}>
                      <td>{i === 0 ? "🥇 " : i === 1 ? "🥈 " : i === 2 ? "🥉 " : ""}{c.name}</td>
                      <td>{c.totalChairs / c.count}</td>
                      <td className="font-weight-bold text-success">{c.efficiency.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>

        {/* Detailed Tables */}
        <div className="advanced-reports-list">
          {/* Wellness */}
          {(selectedReportType === "All" || selectedReportType === "Wellness") && (
            <Card className="report-list-card wellness-border">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Rapports Wellness</h3>
                <div className={`comparison-badge ${getEvolution(wellTotals.totalV, getPrevPeriodStats.wellness).up ? 'up' : 'down'}`}>
                  {getEvolution(wellTotals.totalV, getPrevPeriodStats.wellness).up ? '↑' : '↓'} {getEvolution(wellTotals.totalV, getPrevPeriodStats.wellness).val}% vs prév.
                </div>
              </div>
              <Row className="mb-4 g-4">
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Répartition Recettes/Charges</h6>
                    <div style={{ height: 250 }}>
                      <PieChart
                        series={[{
                          data: [
                            { id: 0, value: wellTotals.totalV, label: 'Recettes', color: '#05cd99' },
                            { id: 1, value: wellTotals.totalE + marginStats.Wellness.cost, label: 'Charges', color: '#ee5d50' }
                          ],
                          innerRadius: 60,
                        }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Tendance Quotidienne</h6>
                    <div style={{ height: 250 }}>
                      <LineChart
                        xAxis={[{ scaleType: 'point', data: trendWellness.map(d => d.date) }]}
                        series={[{ data: trendWellness.map(d => d.value), color: '#05cd99', area: true }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Chiffre par Jour de Semaine</h6>
                    <div style={{ height: 250 }}>
                      <BarChart
                        xAxis={[{ scaleType: 'band', data: distWell.map(d => d.day) }]}
                        series={[{ data: distWell.map(d => d.value), color: '#05cd99' }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="summary-box h-100">
                    <p className="mb-3 fw-bold">Analyse Wellness</p>
                    <div className="d-flex justify-content-between mb-2"><span>Recettes:</span> <span className="text-primary fw-bold">{wellTotals.totalV.toFixed(3)}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span>Dépenses:</span> <span className="text-danger">{wellTotals.totalE.toFixed(3)}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span>Stock:</span> <span className="text-warning">{marginStats.Wellness.cost.toFixed(3)}</span></div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold fs-5"><span>Net:</span> <span className="text-success">{(wellTotals.totalV - (wellTotals.totalE + marginStats.Wellness.cost)).toFixed(3)}</span></div>
                  </div>
                </Col>
              </Row>
              <Table responsive hover className="advanced-table">
                <thead><tr><th>Date</th><th>Localisation</th><th>Recettes</th><th>Dépenses</th><th>Action</th></tr></thead>
                <tbody>
                  {wellnessReports.length > 0 ? wellnessReports.map((r, i) => (
                    <tr key={i}>
                      <td>{moment(r.DateCollect).format('DD/MM/YYYY')}</td>
                      <td>{r.localisation}</td>
                      <td className="text-success">{r.Amount?.toFixed(3)}</td>
                      <td className="text-danger">{r.MonthExpenses?.toFixed(3)}</td>
                      <td><Button variant="outline-primary" size="sm" onClick={() => openDetail(r, "Wellness")}>Détails</Button></td>
                    </tr>
                  )) : <tr><td colSpan="5" className="text-center">Aucun rapport Wellness trouvé</td></tr>}
                </tbody>
              </Table>
            </Card>
          )}

          {/* BagnGo */}
          {(selectedReportType === "All" || selectedReportType === "BagnGo") && (
            <Card className="report-list-card bagngo-border">
              <h3>Rapports BagnGo</h3>
              <Row className="mb-4 g-4">
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Ventes vs Coûts</h6>
                    <div style={{ height: 250 }}>
                      <PieChart
                        series={[{
                          data: [
                            { id: 0, value: finalBagngoIncome, label: 'Ventes', color: '#f97316' },
                            { id: 1, value: bngTotals.totalE + marginStats.BagnGo.cost, label: 'Charges', color: '#ee5d50' }
                          ],
                          innerRadius: 60,
                        }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Tendance de CA</h6>
                    <div style={{ height: 250 }}>
                      <LineChart
                        xAxis={[{ scaleType: 'point', data: trendBagnGo.map(d => d.date) }]}
                        series={[{ data: trendBagnGo.map(d => d.value), color: '#f97316' }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Ventes par Jour</h6>
                    <div style={{ height: 250 }}>
                      <BarChart
                        xAxis={[{ scaleType: 'band', data: distBng.map(d => d.day) }]}
                        series={[{ data: distBng.map(d => d.value), color: '#f97316' }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="summary-box h-100">
                    <p className="mb-3 fw-bold">Analyse BagnGo</p>
                    <div className="d-flex justify-content-between mb-2"><span>Ventes POS:</span> <span className="text-success fw-bold">{marginStats.BagnGo.sales.toFixed(3)}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span>Recettes Rapports:</span> <span className="text-primary">{bngTotals.totalV.toFixed(3)}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span>Stock/Charges:</span> <span className="text-warning">{(bngTotals.totalE + marginStats.BagnGo.cost).toFixed(3)}</span></div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold fs-5"><span>Bénéfice Net:</span> <span>{(finalBagngoIncome - (bngTotals.totalE + marginStats.BagnGo.cost)).toFixed(3)}</span></div>
                  </div>
                </Col>
              </Row>
              <Table responsive hover className="advanced-table">
                <thead><tr><th>Date</th><th>Localisation</th><th>Ventes</th><th>Dépenses</th><th>Action</th></tr></thead>
                <tbody>
                  {bagngoReports.length > 0 ? bagngoReports.map((r, i) => (
                    <tr key={i}>
                      <td>{moment(r.DateCollect).format('DD/MM/YYYY')}</td>
                      <td>{r.localisation}</td>
                      <td className="text-success">{r.Amount?.toFixed(3) || "0.000"}</td>
                      <td className="text-danger">{r.MonthExpenses?.toFixed(3)}</td>
                      <td><Button variant="outline-primary" size="sm" onClick={() => openDetail(r, "BagnGo")}>Détails</Button></td>
                    </tr>
                  )) : <tr><td colSpan="5" className="text-center">Aucun rapport BagnGo trouvé</td></tr>}
                </tbody>
              </Table>
            </Card>
          )}

          {/* E-Space */}
          {(selectedReportType === "All" || selectedReportType === "Espace") && (
            <Card className="report-list-card espace-border">
              <h3>Rapports E-space</h3>
              <Row className="mb-4 g-4">
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Répartition CA</h6>
                    <div style={{ height: 250 }}>
                      <PieChart
                        series={[{
                          data: [
                            { id: 0, value: finalEspaceIncome, label: 'CA', color: '#0ea5e9' },
                            { id: 1, value: espTotals.totalE + marginStats.Espace.cost, label: 'Charges', color: '#ee5d50' }
                          ],
                          innerRadius: 60,
                        }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Trend E-space</h6>
                    <div style={{ height: 250 }}>
                      <LineChart
                        xAxis={[{ scaleType: 'point', data: trendEspace.map(d => d.date) }]}
                        series={[{ data: trendEspace.map(d => d.value), color: '#0ea5e9', area: true }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">CA par Jour</h6>
                    <div style={{ height: 250 }}>
                      <BarChart
                        xAxis={[{ scaleType: 'band', data: distEsp.map(d => d.day) }]}
                        series={[{ data: distEsp.map(d => d.value), color: '#0ea5e9' }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="summary-box h-100">
                    <p className="mb-3 fw-bold">Analyse E-space</p>
                    <div className="d-flex justify-content-between mb-2"><span>Total CA:</span> <span className="text-primary fw-bold">{finalEspaceIncome.toFixed(3)}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span>Total Charges:</span> <span className="text-danger">{(espTotals.totalE + marginStats.Espace.cost).toFixed(3)}</span></div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold fs-5"><span>Net:</span> <span className="text-info">{(finalEspaceIncome - (espTotals.totalE + marginStats.Espace.cost)).toFixed(3)}</span></div>
                  </div>
                </Col>
              </Row>
              <Table responsive hover className="advanced-table">
                <thead><tr><th>Date</th><th>Localisation</th><th>Ventes</th><th>Dépenses</th><th>Action</th></tr></thead>
                <tbody>
                  {espaceReports.map((r, i) => (
                    <tr key={i}>
                      <td>{moment(r.DateCollect).format('DD/MM/YYYY')}</td><td>{r.localisation}</td>
                      <td className="text-success">{r.Amount?.toFixed(3)}</td><td className="text-danger">{r.MonthExpenses?.toFixed(3)}</td>
                      <td><Button variant="outline-primary" size="sm" onClick={() => openDetail(r, "Espace")}>Détails</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {/* Twin Park - Table removed per user request, details are in the summary card above */}
          {selectedReportType === "TwinPark" && (
            <div className="text-center mb-5">
              <p className="text-muted italic">Les détails journaliers de Twin Park ont été masqués. Utilisez la carte de synthèse en haut de page pour l'analyse par catégorie.</p>
            </div>
          )}

          {/* Spintoss */}
          {(selectedReportType === "All" || selectedReportType === "Spintoss") && (
            <Card className="report-list-card spintoss-border">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Rapports Spintoss</h3>
                <div className={`comparison-badge ${getEvolution(spinTotals.totalV, getPrevPeriodStats.spintoss).up ? 'up' : 'down'}`}>
                  {getEvolution(spinTotals.totalV, getPrevPeriodStats.spintoss).up ? '↑' : '↓'} {getEvolution(spinTotals.totalV, getPrevPeriodStats.spintoss).val}% vs prév.
                </div>
              </div>
              <Row className="mb-4 g-4">
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">CA vs Charges</h6>
                    <div style={{ height: 250 }}>
                      <PieChart
                        series={[{
                          data: [
                            { id: 0, value: finalSpintossIncome, label: 'CA', color: '#8b5cf6' },
                            { id: 1, value: spinTotals.totalE + marginStats.Spintoss.cost, label: 'Charges', color: '#ee5d50' }
                          ],
                          innerRadius: 60,
                        }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">Tendance Spintoss</h6>
                    <div style={{ height: 250 }}>
                      <LineChart
                        xAxis={[{ scaleType: 'point', data: trendSpintoss.map(d => d.date) }]}
                        series={[{ data: trendSpintoss.map(d => d.value), color: '#8b5cf6' }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="chart-container-wrapper">
                    <h6 className="text-center mb-3">CA par Jour</h6>
                    <div style={{ height: 250 }}>
                      <BarChart
                        xAxis={[{ scaleType: 'band', data: distSpin.map(d => d.day) }]}
                        series={[{ data: distSpin.map(d => d.value), color: '#8b5cf6' }]}
                      />
                    </div>
                  </div>
                </Col>
                <Col lg={3}>
                  <div className="summary-box h-100">
                    <p className="mb-3 fw-bold">Analyse Spintoss</p>
                    <div className="d-flex justify-content-between mb-2"><span>Total CA:</span> <span className="text-primary fw-bold">{finalSpintossIncome.toFixed(3)}</span></div>
                    <div className="d-flex justify-content-between mb-2"><span>Charges/Coûts:</span> <span className="text-danger">{(spinTotals.totalE + marginStats.Spintoss.cost).toFixed(3)}</span></div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold fs-5"><span>Net:</span> <span style={{color: '#8b5cf6'}}>{(finalSpintossIncome - (spinTotals.totalE + marginStats.Spintoss.cost)).toFixed(3)}</span></div>
                  </div>
                </Col>
              </Row>
              <Table responsive hover className="advanced-table">
                <thead><tr><th>Date</th><th>Localisation</th><th>Total</th><th>Dépenses</th><th>Action</th></tr></thead>
                <tbody>
                  {filteredSpintoss.map((r, i) => (
                    <tr key={i}>
                      <td>{moment(r.DateCollect).format('DD/MM/YYYY')}</td><td>{r.localisation}</td>
                      <td className="text-success">{r.TotalAmount?.toFixed(3)}</td><td className="text-danger">{r.DailyExpenses?.toFixed(3)}</td>
                      <td><Button variant="outline-primary" size="sm" onClick={() => openDetail(r, "Spintoss")}>Détails</Button></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}

          {/* Twin Toys POS Details */}
          {(selectedReportType === "All" || selectedReportType === "TwinToys") && (
            <Card className="report-list-card twintoys-border mt-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>Détails POS Twin Toys</h3>
                <span className="badge bg-info">Somme des 3 localisations</span>
              </div>
              
              <Row className="mb-4">
                <Col lg={7}>
                  <Table responsive hover className="advanced-table">
                    <thead>
                      <tr>
                        <th>Emplacement</th>
                        <th>Chiffre d'Affaires</th>
                        <th>Coût Articles</th>
                        <th>Marge Brute</th>
                        <th>Part (%)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(marginStats.TwinToys?.locations || {}).map(loc => {
                        const data = marginStats.TwinToys.locations[loc];
                        const margin = data.sales - data.cost;
                        const percentage = marginStats.TwinToys.sales > 0 ? (data.sales / marginStats.TwinToys.sales) * 100 : 0;
                        return (
                          <tr key={loc}>
                            <td className="fw-bold">{loc}</td>
                            <td className="text-success">{data.sales.toFixed(3)} DT</td>
                            <td className="text-warning">{data.cost.toFixed(3)} DT</td>
                            <td className="fw-bold">{margin.toFixed(3)} DT</td>
                            <td>{percentage.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                      <tr className="table-info fw-bold">
                        <td>TOTAL TWIN TOYS</td>
                        <td>{marginStats.TwinToys?.sales?.toFixed(3) || "0.000"} DT</td>
                        <td>{marginStats.TwinToys?.cost?.toFixed(3) || "0.000"} DT</td>
                        <td>{((marginStats.TwinToys?.sales || 0) - (marginStats.TwinToys?.cost || 0)).toFixed(3)} DT</td>
                        <td>100%</td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
                <Col lg={5}>
                  <div className="chart-card-advanced p-2">
                    <h5 className="text-center mb-3">Répartition du CA par Localisation</h5>
                    <div style={{ height: 300 }}>
                      <PieChart
                        series={[{
                          data: Object.keys(marginStats.TwinToys?.locations || {}).map((loc, i) => ({
                            id: i,
                            value: marginStats.TwinToys.locations[loc].sales,
                            label: loc
                          })),
                          innerRadius: 40,
                          paddingAngle: 2,
                          cornerRadius: 4,
                        }]}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}
        </div>
      </Container>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton className="detail-modal-header">
          <Modal.Title>Détails du Rapport - {selectedReportDetail?.type}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="detail-modal-body">
          <div className="detail-grid">
            {renderDetailFields()}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdvancedAnalytics;
